// Structural shape shared by both apps' generated TurbineComponent types, so this
// helper serves the management and field clients without depending on either.
export type ComponentInfo = {
  id?: string;
  component_type?: string | null;
  serial_number?: string | null;
  model?: string | null;
  set_number?: string | null;
};

export const COMPONENT_TYPE_ORDER = ["BLADE", "NACELLE", "HUB", "TOWER"] as const;
export type ComponentType = (typeof COMPONENT_TYPE_ORDER)[number];

const COMPONENT_TYPE_LABELS: Record<ComponentType, string> = {
  BLADE: "Blade",
  NACELLE: "Nacelle",
  HUB: "Hub",
  TOWER: "Tower",
};

export function isBlade(component: ComponentInfo | null | undefined): boolean {
  return componentTypeOf(component) === "BLADE";
}

export function componentTypeLabel(componentType: string | null | undefined): string {
  return COMPONENT_TYPE_LABELS[componentTypeOf({ component_type: componentType })];
}

export function componentTypeMessageId(componentType: string | null | undefined): string {
  return `component_type_${componentTypeOf({ component_type: componentType })}`;
}

// "Blade A" / "Nacelle" / "Tower · SN123" — the primary identifier shown wherever a
// component is named (selects, tables, session cards, notifications, clipboard).
export function formatComponentLabel(
  component: ComponentInfo | null | undefined
): string {
  const typeLabel = componentTypeLabel(component?.component_type);
  const serialNumber = component?.serial_number?.trim();
  if (!serialNumber) return typeLabel;
  return isBlade(component) ? `${typeLabel} ${serialNumber}` : `${typeLabel} · ${serialNumber}`;
}

// Secondary details (model/set) that accompany the label.
export function formatComponentDetails(
  component: ComponentInfo | null | undefined
): string {
  return [
    component?.model,
    component?.set_number ? `Set ${component.set_number}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function sortComponents<T extends ComponentInfo>(components: T[]): T[] {
  return [...components].sort((a, b) => {
    const typeDelta =
      COMPONENT_TYPE_ORDER.indexOf(componentTypeOf(a)) -
      COMPONENT_TYPE_ORDER.indexOf(componentTypeOf(b));
    if (typeDelta !== 0) return typeDelta;
    return (a.serial_number ?? "").localeCompare(b.serial_number ?? "");
  });
}

export type ComponentSelectOption = { key: string; value: string; label: string };
export type ComponentSelectGroup = {
  label: string;
  title: string;
  options: ComponentSelectOption[];
};

// Antd Select `options` grouped by component type, in COMPONENT_TYPE_ORDER.
// `groupLabel` lets callers pass an intl-translated heading for each type.
export function groupComponentOptions(
  components: ComponentInfo[],
  groupLabel: (componentType: ComponentType) => string = componentTypeLabel
): ComponentSelectGroup[] {
  return COMPONENT_TYPE_ORDER.map((componentType) => {
    const options = sortComponents(
      components.filter((component) => componentTypeOf(component) === componentType)
    ).flatMap((component) =>
      component.id
        ? [{ key: component.id, value: component.id, label: formatComponentLabel(component) }]
        : []
    );
    return { label: groupLabel(componentType), title: groupLabel(componentType), options };
  }).filter((group) => group.options.length > 0);
}

function componentTypeOf(component: ComponentInfo | null | undefined): ComponentType {
  const componentType = component?.component_type;
  return COMPONENT_TYPE_ORDER.includes(componentType as ComponentType)
    ? (componentType as ComponentType)
    : "BLADE";
}
